import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, Search, ClipboardList, Handshake, Clock, Radio, ReceiptEuro } from 'lucide-react';
import Wordmark from '../components/Wordmark';

/* ── Backstage Lume palette (mandated, single source of truth) ── */
const C = {
  bg:      '#0E1110',
  accent:  '#C8923E',
  text:    '#F0EDE4',
  muted:   'rgba(240,237,228,0.45)',
  faint:   'rgba(240,237,228,0.25)',
  checkMuted: 'rgba(240,237,228,0.35)',  /* free-tier checks — de-emphasised vs amber Pro */
  stopLabel:  'rgba(240,237,228,0.5)',   /* "Others stop here" drop-off markers */
  border:  'rgba(240,237,228,0.08)',     /* canonical card border — used by EVERY card */
  card:    'rgba(240,237,228,0.04)',
  cardPro: 'rgba(240,237,228,0.07)',
  /* subtle gold hint — a warm wash fading down + a soft amber lift */
  hintWash: 'linear-gradient(180deg, rgba(200,146,62,0.08) 0%, rgba(200,146,62,0) 55%)',
  hintGlow: '0 12px 40px -12px rgba(200,146,62,0.18)',
  /* amber-tinted callout / pill surfaces */
  amberTint:   'rgba(200,146,62,0.08)',
  amberBorder: 'rgba(200,146,62,0.20)',
  amberGlow:   '0 0 0 1px rgba(200,146,62,0.4)',
};

/* Display sizes — hero header larger, section headers a step down */
const HERO_FS    = 'clamp(40px, 8vw, 64px)';
const SECTION_FS = 'clamp(32px, 6vw, 48px)';

/* Single source of truth for vertical rhythm between major sections */
const SECTION_GAP = '96px';

/* Border used by every card on the page — no exceptions */
const CARD_BORDER = `1px solid ${C.border}`;

const REDUCED = typeof window !== 'undefined' && window.matchMedia
  ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
  : false;

/* ─────────────────────────────────────────────────────────────────────────
   Scroll-reveal plumbing — IntersectionObserver, fires once
   ───────────────────────────────────────────────────────────────────────── */
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (REDUCED || !el) { setInView(true); return; }
    // Immediate check — reveal anything already within the viewport on mount. Covers
    // above-the-fold sections and environments where IntersectionObserver is suspended
    // (hidden/background tabs), so content is never stranded at opacity 0.
    const r = el.getBoundingClientRect();
    if (r.top < window.innerHeight && r.bottom > 0) { setInView(true); return; }
    if (typeof IntersectionObserver === 'undefined') { setInView(true); return; }
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold: 0.15 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

/* fade-in + slide-up (opacity 0→1, translateY 20px→0, 0.5s ease) */
const revealStyle = (inView) => REDUCED ? {} : {
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(20px)',
  transition: 'opacity 0.5s ease, transform 0.5s ease',
};

/* Wrapper that reveals its children on scroll — used for the inline page sections */
function Reveal({ children, className, style }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{ ...style, ...revealStyle(inView) }}>
      {children}
    </div>
  );
}

/* Number that counts up from 0 → target when `start` flips true (1.2s, easeOutCubic) */
function CountUp({ prefix = '', target, suffix = '', start, duration = 1200 }) {
  const [val, setVal] = useState(REDUCED ? target : 0);
  useEffect(() => {
    if (REDUCED) { setVal(target); return; }
    if (!start) return;
    // requestAnimationFrame is throttled in hidden tabs — show the final figure.
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') { setVal(target); return; }
    let raf;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      setVal(Math.round(ease(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start, target, duration]);
  return <span>{prefix}{val}{suffix}</span>;
}

/* ─────────────────────────────────────────────────────────────────────────
   SECTION 2 — TIER CARDS
   ───────────────────────────────────────────────────────────────────────── */
const SECTIONS = [
  {
    audience: 'Musician / Tech Crew',
    tiers: [
      {
        name: 'Free',
        price: '€0',
        period: '/month',
        pro: false,
        mutedChecks: true,
        cta: { label: 'Sign up free', variant: 'ghost' },
        features: [
          'Public profile, get found in the directory',
          'Browse and apply to open requests',
          'Basic gig history',
          'Rider fit check: 3 per month',
        ],
      },
      {
        name: 'Pro',
        price: '€8',
        period: '/month',
        pro: true,
        highlight: true,
        cta: { label: 'Get started', variant: 'solid' },
        features: [
          'Everything in Free',
          'Full operational record with trust signals',
          'Unlimited rider fit checks + check history',
          'Structured confirmation flow (digital handshake)',
          'Extended search radius: 40km+',
          'Rate benchmarking',
          'Finance & admin tools (pro forma invoicing, tax export)',
        ],
      },
    ],
  },
  {
    audience: 'Venue',
    tiers: [
      {
        name: 'Free',
        price: '€0',
        period: '/month',
        pro: false,
        mutedChecks: true,
        cta: { label: 'Sign up free', variant: 'ghost' },
        features: [
          'Publish structured gig requests',
          'Browse and message musicians and tech crew',
          'Basic advancing fields',
          'Rider fit check: 3 per month',
          'One-click reboot',
        ],
      },
      {
        name: 'Pro',
        price: '€99',
        period: '/month',
        pro: true,
        popular: true,
        cta: { label: 'Get started', variant: 'solid' },
        features: [
          'Everything in Free',
          'Emergency substitute flow (<72h window)',
          'Full dashboard and analytics',
          'Multi-room support',
          'Showtime: live call sheet, confirmation status, real-time operational overview',
        ],
      },
    ],
  },
];

function TierCard({ tier }) {
  const ctaBase = 'mt-7 w-full inline-flex items-center justify-center transition-all';
  const ctaStyle = tier.cta.variant === 'solid'
    ? { background: C.accent, color: C.bg, border: '1px solid transparent' }
    : { background: 'transparent', color: C.text, border: `1px solid ${C.text}` };

  return (
    <div className="relative flex flex-col">
      {/* MOST POPULAR badge — sits above the Venue Pro card */}
      {tier.popular && (
        <div
          className="absolute left-1/2 -translate-x-1/2 -top-3 z-10"
          style={{
            fontFamily: 'var(--s8-font-mono)', fontSize: '10px', letterSpacing: '0.14em',
            textTransform: 'uppercase', color: C.bg, background: C.accent,
            padding: '4px 12px', borderRadius: 'var(--s8-radius-pill)', fontWeight: 600,
          }}
          data-testid="pricing-badge-popular"
        >
          Most popular
        </div>
      )}

      <div
        className="flex flex-col flex-1 p-6 sm:p-7"
        style={{
          background: tier.highlight ? `${C.hintWash}, ${C.cardPro}` : tier.pro ? C.cardPro : C.card,
          border: CARD_BORDER,
          borderRadius: 'var(--s8-radius-lg)',
          boxShadow: tier.popular ? C.amberGlow : tier.highlight ? C.hintGlow : 'none',
        }}
        data-testid={`pricing-card-${tier.name.toLowerCase()}`}
      >
        {/* Tier name */}
        <div style={{ fontFamily: 'var(--s8-font-condensed)', textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: '13px', color: C.muted }}>
          {tier.name}
        </div>

        {/* Price */}
        <div className="flex items-baseline gap-1.5 mt-1.5 mb-6">
          <span style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontVariantNumeric: 'tabular-nums', fontSize: '48px', lineHeight: 1.15, color: C.text }}>{tier.price}</span>
          {tier.period && <span style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '14px', color: C.muted }}>{tier.period}</span>}
        </div>

        {/* Features */}
        <ul className="flex flex-col gap-3 flex-1">
          {tier.features.map((feature) => (
            <li key={feature} className="flex gap-2.5 items-start" style={{ fontSize: '14px', lineHeight: 1.5 }}>
              <Check size={16} strokeWidth={2.5} style={{ color: tier.mutedChecks ? C.checkMuted : C.accent, flexShrink: 0, marginTop: '2px' }} aria-hidden="true" />
              <span style={{ color: C.text }}>{feature}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <Link
          to="/signup"
          className={ctaBase}
          style={{ ...ctaStyle, padding: '12px 16px', borderRadius: 'var(--s8-radius-md)', fontFamily: 'var(--s8-font-body)', fontWeight: 600, fontSize: '14px', letterSpacing: '-0.01em' }}
          data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
        >
          {tier.cta.label}
        </Link>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   SECTION 3 — GIG LIFECYCLE VISUALIZATION
   ───────────────────────────────────────────────────────────────────────── */
const CHAPTERS = [
  {
    n: 1, name: 'DISCOVERY', icon: Search,
    does: 'Venues post structured requests. Musicians and crew apply.',
    stops: 'Gigmit, Gigstarter, Sonicbids',
  },
  {
    n: 2, name: 'EXPECTATION SETTING', icon: ClipboardList,
    does: 'Pay, travel, gear, arrival time, set duration — all locked in before anyone commits.',
    stops: 'most platforms',
  },
  {
    n: 3, name: 'DEAL', icon: Handshake,
    does: 'Digital handshake. Both parties confirm every field explicitly. Nothing stays verbal.',
    stops: null,
  },
  {
    n: 4, name: 'PRE-SHOW', icon: Clock,
    does: 'Rider fit check, call sheet, confirmation status per person.',
    stops: null,
  },
  {
    n: 5, name: 'SHOW DAY', icon: Radio, wide: true, pill: 'UNIQUE TO STAGE8',
    does: 'People + equipment in one view. Who is confirmed, what they need, what the venue provides. Live operational overview.',
    stops: 'Artifax (manages spaces, not talent)',
  },
  {
    n: 6, name: 'ADMIN & PAYMENT', icon: ReceiptEuro,
    does: 'Pro forma invoicing, payment speed tracking, tax export, cancellation history.',
    stops: null,
  },
];

function ChapterCard({ chapter }) {
  const Icon = chapter.icon;
  return (
    <div
      className={`lifecycle-card relative z-[1] flex flex-col p-5 shrink-0 lg:min-w-0 ${chapter.wide ? 'lifecycle-card--wide w-[322px] min-h-[360px] lg:min-h-[400px]' : 'w-[230px]'}`}
      style={{
        background: chapter.wide ? `${C.hintWash}, ${C.cardPro}` : C.card,
        border: CARD_BORDER,
        borderRadius: 'var(--s8-radius-lg)',
        boxShadow: chapter.wide ? C.amberGlow : 'none',
      }}
      data-testid={`lifecycle-chapter-${chapter.n}`}
    >
      {/* Icon — opaque node so the connecting line passes behind it, not through the glyph */}
      <div
        className="flex items-center justify-center mb-4"
        style={{ width: '40px', height: '40px', borderRadius: 'var(--s8-radius-md)', background: `linear-gradient(rgba(200,146,62,0.12), rgba(200,146,62,0.12)), ${C.bg}`, border: `1px solid ${C.amberBorder}` }}
      >
        <Icon size={20} strokeWidth={2} style={{ color: C.accent }} aria-hidden="true" />
      </div>

      {/* Chapter index + name */}
      <div style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '10px', letterSpacing: '0.12em', color: C.faint, marginBottom: '4px' }}>
        CHAPTER {chapter.n}
      </div>
      <h3 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '24px', lineHeight: 1.15, letterSpacing: '-0.01em', color: C.text }}>
        {chapter.name}
      </h3>

      {/* UNIQUE TO STAGE8 pill */}
      {chapter.pill && (
        <span
          className="inline-flex self-start mt-2.5"
          style={{ fontFamily: 'var(--s8-font-mono)', fontSize: '10px', letterSpacing: '0.1em', color: C.bg, background: C.accent, padding: '3px 9px', borderRadius: 'var(--s8-radius-pill)', fontWeight: 600 }}
        >
          {chapter.pill}
        </span>
      )}

      {/* What Stage8 does */}
      <p className="mt-3" style={{ fontSize: '13px', lineHeight: 1.5, color: C.muted }}>
        {chapter.does}
      </p>

      {/* Drop-off marker */}
      {chapter.stops && (
        <div
          className="mt-4 pt-3"
          style={{ borderTop: CARD_BORDER, fontFamily: 'var(--s8-font-mono)', fontSize: '11px', lineHeight: 1.45, letterSpacing: '-0.01em', color: C.stopLabel }}
          data-testid={`lifecycle-stops-${chapter.n}`}
        >
          Others stop here: {chapter.stops}
        </div>
      )}
    </div>
  );
}

function LifecycleSection() {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} style={{ marginTop: SECTION_GAP, ...revealStyle(inView) }} data-testid="lifecycle-section">
      <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: SECTION_FS, lineHeight: 1.15, letterSpacing: '-0.01em', color: C.text }}>
        THE FULL GIG. START TO FINISH.
      </h2>
      <p className="mt-3 mb-10 sm:mb-12" style={{ color: C.muted, fontSize: '16px', maxWidth: '52ch' }}>
        Most tools cover one chapter. Stage8 covers all of them.
      </p>

      {/* Horizontal track — full-width on desktop, horizontal scroll on mobile */}
      <div className="overflow-x-auto lg:overflow-visible pb-4" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="lifecycle-track">
        <div className="relative flex items-stretch lg:items-start gap-4 lg:gap-3 w-max lg:w-full">
          {/* Amber connecting line — runs through the icon row, fills L→R on scroll-in */}
          <div
            aria-hidden="true"
            className="absolute left-0 right-0"
            style={{
              top: '40px',
              height: '1px',
              background: 'rgba(200,146,62,0.3)',
              zIndex: 0,
              transformOrigin: 'left center',
              transform: inView ? 'scaleX(1)' : 'scaleX(0)',
              transition: REDUCED ? 'none' : 'transform 0.8s ease',
            }}
            data-testid="lifecycle-connector"
          />
          {CHAPTERS.map((chapter) => (
            <ChapterCard key={chapter.n} chapter={chapter} />
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   SECTION 4 — THE NUMBERS MAKE THE CASE
   ───────────────────────────────────────────────────────────────────────── */
const TALENT_PLATFORMS = [
  { name: 'Gigmit', line: 'Discovery and matching. Stops at the booking.' },
  { name: 'Gigstarter', line: 'Browse and apply. No operational layer.' },
  { name: 'Sonicbids', line: 'EPK submissions. No coordination tools.' },
];

/* All six lifecycle chapters, compressed to a brief line each */
const STAGE8_FLOW = [
  'Discovery — venues post, talent applies',
  'Expectation setting — terms locked up front',
  'Deal — digital handshake, both confirm',
  'Pre-show — rider fit, call sheet, status',
  'Show day — live operational overview',
  'Admin & payment — invoicing, tax, history',
];

/* prefix + number + suffix so the figure can count up to 600+ / €178K / +3% */
const STATS = [
  { prefix: '',  number: 600, suffix: '+', label: 'live music venues and active bookers in the Netherlands (375 dedicated venues + cafes, cultural centres, and regular live music programmers)' },
  { prefix: '€', number: 178, suffix: 'K', label: 'ARR from venue subscriptions at 30% Pro adoption' },
  { prefix: '+', number: 3,   suffix: '%', label: 'transaction fee on confirmed bookings — growth-stage upside' },
];

/* shared text styles for the positioning strip */
const colLabel = { fontFamily: 'var(--s8-font-condensed)', textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: '13px', color: C.muted };
const colBody  = { fontSize: '13px', lineHeight: 1.55, color: 'rgba(240,237,228,0.55)' };
const colPunch = { fontFamily: 'var(--s8-font-body)', fontSize: '14px', fontWeight: 700, color: C.accent };

function NumbersSection() {
  const [ref, inView] = useInView();
  const [statRef, statsInView] = useInView();
  const divider = 'border-t lg:border-t-0 lg:border-l border-[rgba(240,237,228,0.08)] pt-7 lg:pt-0 mt-7 lg:mt-0 lg:px-8';

  return (
    <section ref={ref} style={{ marginTop: SECTION_GAP, ...revealStyle(inView) }} data-testid="numbers-section">
      <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: SECTION_FS, lineHeight: 1.15, letterSpacing: '-0.01em', color: C.text }}>
        THE NUMBERS MAKE THE CASE
      </h2>

      {/* ── PART 1 — Positioning strip: 3 equal columns, vertical dividers ── */}
      <div className="mt-10 sm:mt-12 grid lg:grid-cols-3 items-stretch" data-testid="positioning-strip">
        {/* LEFT — Talent platforms */}
        <div className="flex flex-col lg:pr-8" data-testid="positioning-talent">
          <h3 style={colLabel}>Talent Platforms</h3>
          <div className="mt-4 flex flex-col gap-3">
            {TALENT_PLATFORMS.map((p) => (
              <p key={p.name} style={colBody}>
                <span style={{ color: 'rgba(240,237,228,0.75)', fontWeight: 600 }}>{p.name}</span> — {p.line}
              </p>
            ))}
          </div>
          <p className="mt-auto pt-6" style={colPunch}>Stop at the match.</p>
        </div>

        {/* CENTER — Stage8 */}
        <div className={`flex flex-col ${divider}`} data-testid="positioning-stage8">
          <h3 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '24px', letterSpacing: '-0.01em', color: C.accent, lineHeight: 1.15 }}>
            STAGE8
          </h3>
          <p className="mt-1.5" style={{ fontSize: '13px', lineHeight: 1.5, color: C.muted }}>
            The coordination layer between talent and venue.
          </p>
          <ul className="mt-4 flex flex-col gap-2">
            {STAGE8_FLOW.map((step) => (
              <li key={step} className="flex gap-2 items-baseline" style={colBody}>
                <span aria-hidden="true" style={{ color: C.accent, fontSize: '10px', lineHeight: 1.6, flexShrink: 0 }}>●</span>
                <span style={{ color: 'rgba(240,237,228,0.7)' }}>{step}</span>
              </li>
            ))}
          </ul>
          <p className="mt-auto pt-6" style={colPunch}>Covers the full gig.</p>
        </div>

        {/* RIGHT — Venue software */}
        <div className={`flex flex-col ${divider}`} data-testid="positioning-venue">
          <h3 style={colLabel}>Venue Software</h3>
          <div className="mt-4 flex flex-col gap-1.5">
            <p style={colBody}>
              <span style={{ color: 'rgba(240,237,228,0.75)', fontWeight: 600 }}>Artifax</span> — Full venue and space management.
            </p>
            <p style={colBody}>Does not touch talent coordination.</p>
            <p style={colBody}>Starts at €250/user/month + €15,000 implementation.</p>
            <p className="mt-3" style={colBody}>
              <span style={{ color: 'rgba(240,237,228,0.75)', fontWeight: 600 }}>Momentus</span> — Enterprise venue operations for arenas and convention centers.
            </p>
            <p style={colBody}>Powers 4,000+ venues globally.</p>
            <p style={colBody}>Manages spaces, catering, AV, and finance.</p>
            <p style={colBody}>Does not touch talent coordination.</p>
            <p style={colBody}>Priced and scoped for organisations with dedicated IT teams.</p>
          </div>
          <p className="mt-auto pt-6" style={colPunch}>Ignores the talent.</p>
        </div>
      </div>

      {/* ── PART 2 — ARR math callout ── */}
      <div
        ref={statRef}
        className="mt-10 sm:mt-12"
        style={{
          background: 'rgba(200,146,62,0.06)',
          border: '1px solid rgba(200,146,62,0.15)',
          borderRadius: '8px',
          padding: '28px 32px',
        }}
        data-testid="arr-callout"
      >
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {STATS.map((s) => (
            <div
              key={s.suffix + s.number}
              className="text-center flex flex-col items-center"
              style={{ background: C.card, border: CARD_BORDER, borderRadius: 'var(--s8-radius-lg)', padding: '20px' }}
              data-testid={`arr-stat-${s.prefix}${s.number}${s.suffix}`}
            >
              <div style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontVariantNumeric: 'tabular-nums', fontSize: '48px', lineHeight: 1.15, color: C.accent }}>
                <CountUp prefix={s.prefix} target={s.number} suffix={s.suffix} start={statsInView} />
              </div>
              <div className="mt-2" style={{ fontSize: '12px', lineHeight: 1.45, color: C.muted, maxWidth: '24ch' }}>{s.label}</div>
            </div>
          ))}
        </div>
        <p
          className="mt-8 mx-auto text-center"
          style={{ fontFamily: 'var(--s8-font-body)', fontStyle: 'italic', fontSize: '13px', lineHeight: 1.55, color: 'rgba(240,237,228,0.5)', maxWidth: '64ch' }}
        >
          Artifax starts at €250/user/month + €15,000 onboarding. Stage8 Pro is €99/month. No implementation. No IT team. No contract.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   SECTION — WHAT A CANCELLED SHOW ACTUALLY COSTS
   ───────────────────────────────────────────────────────────────────────── */
const COST_CARDS = [
  { value: '€1,500–3,000', label: 'lost bar and ticket revenue on a typical 200-cap night' },
  { value: '€400–800',     label: 'staff, sound tech, and logistics already paid out' },
  { value: '∞',            label: "reputation damage with a crowd that won't come back" },
];

function CancelledShowSection() {
  const [ref, inView] = useInView();
  return (
    <section
      ref={ref}
      style={{
        /* full-bleed band — break out of the max-w-[1200px] main */
        width: '100vw',
        marginLeft: 'calc(50% - 50vw)',
        marginTop: SECTION_GAP,
        background: 'rgba(200,146,62,0.04)',
        borderTop: '1px solid rgba(200,146,62,0.1)',
        borderBottom: '1px solid rgba(200,146,62,0.1)',
        padding: '60px 0',
        ...revealStyle(inView),
      }}
      data-testid="cancelled-show-section"
    >
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
        {/* Header — two lines, second in amber */}
        <h2 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: SECTION_FS, lineHeight: 1.15, letterSpacing: '-0.01em', color: C.text }}>
          ONE CANCELLED SHOW.<br />
          <span style={{ color: C.accent }}>HERE'S WHAT IT COSTS.</span>
        </h2>

        {/* Three cost cards — neutral tier-card treatment, no amber left border */}
        <div className="mt-10 sm:mt-12 grid gap-5 sm:grid-cols-3" data-testid="cost-cards">
          {COST_CARDS.map((c) => (
            <div
              key={c.label}
              className="flex flex-col p-6 sm:p-7"
              style={{ background: C.card, border: CARD_BORDER, borderRadius: 'var(--s8-radius-lg)' }}
              data-testid="cost-card"
            >
              <div style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontVariantNumeric: 'tabular-nums', fontSize: '40px', lineHeight: 1.15, color: C.accent }}>
                {c.value === '∞' ? <span className="s8-pulse">∞</span> : c.value}
              </div>
              <div className="mt-3" style={{ fontSize: '13px', lineHeight: 1.5, color: C.muted }}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Anchor line — left-aligned to match the section header */}
        <p
          className="mt-10 sm:mt-12"
          style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: '24px', lineHeight: 1.15, letterSpacing: '-0.01em', color: C.text, maxWidth: '34ch' }}
        >
          Stage8 Pro for venues is €99/month. One prevented cancellation pays for six months.
        </p>

        {/* Footnote */}
        <p className="mt-4" style={{ fontSize: '10px', lineHeight: 1.5, color: C.muted, maxWidth: '52ch' }}>
          Revenue estimates based on 200-capacity venue. Actual figures vary by venue size and programme.
        </p>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────────────────────────────────── */
export default function PricingPage() {
  return (
    <div style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: 'var(--s8-font-body)', overflowX: 'hidden' }} data-testid="pricing-page">
      {/* Keyframes — scoped to this page via a one-off style tag */}
      <style>{`
        @keyframes s8-pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.05); } }
        .s8-pulse { display: inline-block; animation: s8-pulse 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .s8-pulse { animation: none; } }
        /* Lifecycle row at desktop: explicit gap-aware widths so Show Day is exactly
           40% wider than its 5 siblings (5·n + 1.4·n + 5·12px gap = 100%). */
        @media (min-width: 1024px) {
          .lifecycle-card { flex: 0 1 auto; min-width: 0; width: calc((100% - 60px) * 0.15625); }
          .lifecycle-card--wide { width: calc((100% - 60px) * 0.21875); }
        }
      `}</style>

      {/* Slim top bar — wordmark home link */}
      <header style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 h-14 flex items-center">
          <Link to="/"><Wordmark className="h-[28px]" /></Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 sm:px-8" style={{ paddingBottom: SECTION_GAP }}>
        {/* ── SECTION 1 — PAGE HEADER (96px symmetric breathing room) ── */}
        <Reveal className="text-center" style={{ paddingTop: SECTION_GAP, marginBottom: SECTION_GAP }}>
          <h1 style={{ fontFamily: 'var(--s8-font-display)', fontWeight: 600, fontSynthesis: 'none', fontSize: HERO_FS, lineHeight: 1.1, letterSpacing: '-0.02em', textWrap: 'balance', color: C.text }}>
            PRICING
          </h1>
          <p className="mt-3 mx-auto" style={{ color: C.muted, fontSize: '16px', maxWidth: '46ch' }}>
            Built for working musicians, crew, and the venues that book them.
          </p>
        </Reveal>

        {/* ── SECTION 2 — TIER CARDS ── */}
        <Reveal className="grid gap-10 lg:gap-14 lg:grid-cols-2">
          {SECTIONS.map((section) => (
            <section key={section.audience} data-testid={`pricing-section-${section.audience.includes('Venue') ? 'venue' : 'talent'}`}>
              <h2 className="mb-4" style={{ fontFamily: 'var(--s8-font-condensed)', textTransform: 'uppercase', letterSpacing: '0.12em', fontSize: '13px', color: C.accent }}>
                {section.audience}
              </h2>
              <div className="grid gap-5 md:grid-cols-2 items-stretch">
                {section.tiers.map((tier) => <TierCard key={tier.name} tier={tier} />)}
              </div>
            </section>
          ))}
        </Reveal>

        {/* ── SECTION 3 — GIG LIFECYCLE ── */}
        <LifecycleSection />

        {/* ── SECTION — WHAT A CANCELLED SHOW ACTUALLY COSTS ── */}
        <CancelledShowSection />

        {/* ── SECTION 5 — THE NUMBERS MAKE THE CASE ── */}
        <NumbersSection />
      </main>
    </div>
  );
}
